import apiClient from './AuthService';

// Types based on backend API
export type DayType = 'WORKING' | 'HOLIDAY';
export type FloorType = 'FIRST_FLOOR' | 'SECOND_FLOOR';

export interface SpatialExperienceRessoChallenge {
  spatial_experience_resso_challenge_id?: string;
  project_id: string;
  user_id?: string;
  member_id: string;
  member_name?: string;
  scene_number: number;
  scene_title?: string | null;
  scene_text?: string | null;
  scene_comments?: string | null;
  day_type: DayType;
  positions?: string | null;
  floor_type: FloorType;
  enclosures?: string | null;
  created_at?: string;
  updated_at?: string;
}

// Interface for avatar positions stored in the positions field
export interface AvatarPosition {
  memberId: string;
  memberName: string;
  x: number;  // percentage 0-100
  y: number;  // percentage 0-100
  color: string;
  floor: FloorType;
}

export interface SpatialExperienceRessoChallengeResponse {
  spatial_experience_resso_challenge_id: string;
  message: string;
}

export interface SpatialExperienceRessoChallengeData {
  data: SpatialExperienceRessoChallenge | SpatialExperienceRessoChallenge[];
}

// Create a new challenge entry
export const saveSpatialExperienceRessoChallenge = async (
  projectId: string,
  memberId: string,
  sceneNumber: number,
  dayType: DayType,
  floorType: FloorType,
  sceneTitle?: string,
  sceneText?: string,
  sceneComments?: string,
  positions?: string,
  enclosures?: string
): Promise<SpatialExperienceRessoChallengeResponse> => {
  try {
    const requestData = {
      project_id: projectId,
      member_id: memberId,
      scene_number: sceneNumber,
      day_type: dayType,
      floor_type: floorType,
      scene_title: sceneTitle || null,
      scene_text: sceneText || null,
      scene_comments: sceneComments || null,
      positions: positions || null,
      enclosures: enclosures || null
    };

    console.log('📤 Sending SpatialExperienceResso data:', requestData);

    const response = await apiClient.post<SpatialExperienceRessoChallengeResponse>(
      '/quiz/spatial-experience-resso-challenge',
      requestData
    );

    console.log('✅ SpatialExperienceResso saved successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ SpatialExperienceResso API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      fullError: error
    });

    if (error.response?.data?.error && typeof error.response.data.error === 'string') {
      throw new Error(error.response.data.error);
    } else if (error.response?.data?.message && typeof error.response.data.message === 'string') {
      throw new Error(error.response.data.message);
    } else if (error.response?.data && typeof error.response.data === 'string') {
      throw new Error(error.response.data);
    }

    throw new Error('Error al guardar el challenge. Por favor, intenta de nuevo.');
  }
};

// Get a single challenge by ID
export const getSpatialExperienceRessoChallenge = async (
  challengeId: string
): Promise<SpatialExperienceRessoChallenge> => {
  try {
    const response = await apiClient.get<SpatialExperienceRessoChallengeData>(
      `/quiz/spatial-experience-resso-challenge/${challengeId}`
    );
    return response.data.data as SpatialExperienceRessoChallenge;
  } catch (error: any) {
    console.error('❌ getSpatialExperienceRessoChallenge error:', error);
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

// Get all challenges for a project
export const getProjectSpatialExperienceRessoChallenges = async (
  projectId: string
): Promise<SpatialExperienceRessoChallenge[]> => {
  try {
    console.log('📡 Fetching spatial experience resso challenges for project:', projectId);
    const response = await apiClient.get<SpatialExperienceRessoChallengeData>(
      `/quiz/project/${projectId}/spatial-experience-resso-challenges`
    );

    const data = response.data.data;
    if (!Array.isArray(data)) {
      return data ? [data] : [];
    }
    return data;
  } catch (error: any) {
    console.error('❌ getProjectSpatialExperienceRessoChallenges error:', error);

    if (error.response?.status === 404) {
      console.log('📡 No challenges found for project (404), returning empty array');
      return [];
    }

    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

// Get all challenges for a member
export const getMemberSpatialExperienceRessoChallenges = async (
  memberId: string
): Promise<SpatialExperienceRessoChallenge[]> => {
  try {
    console.log('📡 Fetching spatial experience resso challenges for member:', memberId);
    const response = await apiClient.get<SpatialExperienceRessoChallengeData>(
      `/quiz/member/${memberId}/spatial-experience-resso-challenges`
    );
    console.log('📡 Response received:', response.data);

    const data = response.data.data;
    if (!Array.isArray(data)) {
      return data ? [data] : [];
    }
    return data;
  } catch (error: any) {
    console.error('❌ getMemberSpatialExperienceRessoChallenges error:', {
      status: error.response?.status,
      data: error.response?.data,
      fullError: error
    });

    if (error.response?.status === 404) {
      console.log('📡 No challenges found for member (404), returning empty array');
      return [];
    }

    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// Helper function to get challenges filtered by day type
export const getMemberChallengesByDayType = async (
  memberId: string,
  dayType: DayType
): Promise<SpatialExperienceRessoChallenge[]> => {
  const challenges = await getMemberSpatialExperienceRessoChallenges(memberId);
  return challenges.filter(c => c.day_type === dayType);
};

// Update an existing challenge (for enclosures phase)
export const updateSpatialExperienceRessoChallenge = async (
  challengeId: string,
  enclosures?: string,
  sceneComments?: string
): Promise<SpatialExperienceRessoChallengeResponse> => {
  try {
    const requestData = {
      enclosures: enclosures || null,
      scene_comments: sceneComments || null
    };

    console.log('📤 Updating SpatialExperienceResso challenge:', challengeId, requestData);

    const response = await apiClient.put<SpatialExperienceRessoChallengeResponse>(
      `/quiz/spatial-experience-resso-challenge/${challengeId}`,
      requestData
    );

    console.log('✅ SpatialExperienceResso updated successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ SpatialExperienceResso Update API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      fullError: error
    });

    if (error.response?.data?.error && typeof error.response.data.error === 'string') {
      throw new Error(error.response.data.error);
    } else if (error.response?.data?.message && typeof error.response.data.message === 'string') {
      throw new Error(error.response.data.message);
    } else if (error.response?.data && typeof error.response.data === 'string') {
      throw new Error(error.response.data);
    }

    throw new Error('Error al actualizar el challenge. Por favor, intenta de nuevo.');
  }
};
